import "./style.css";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { defaultParams } from "@utils/global.constants";
import dropdown from "../../Assets/Images/dropdown-polygon.png";
import { createApplication } from "../../API/Applications";
import {
  APPLICATION_REDIRECT_PATHS,
  getApplicationRedirectUrl,
} from "@utils/redirectUrl";
import {
  useActiveOffersQuery,
  useAllApplicationsQuery,
  useGetUserQuery
} from "../../API/Dashboard";
import useDebounce from "@hooks/useDebounce";
import { useFormik } from "formik";
import { failed, success } from "@components/Common/Toastify";
import * as yup from "yup";
import storage from "@utils/storage";
import groupedcircles from "../../Assets/Images/circle-grp.png";
import axios from "axios";
import envConfig from "../../environment";
import { useSocket } from "@utils/socket/socketContext";
import { Country } from 'country-state-city';
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import { getAnonymousLenderLabel } from "@utils/lenderDisplay";
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap/dist/css/bootstrap.min.css';

const countries = Country.getAllCountries();

type Offer = {
  offers: any[]
  uuid: string;
  lenderId: string;
  title: string;
  loanAmount: string;
  interestRate: string;
  loanDuration: string;
  termsAndConditions: string;
  status: string;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  applications: any[];
};


function BrowseOffers() {
  const navigate = useNavigate();
  const Id = storage.getUser();
  const userId = Id.user.userId;
  const { socket } = useSocket();
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [params, setParams] = useState(defaultParams);
  const debouncedSearch = useDebounce(params.search, 500);
  const [btnLoading, setBtnLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const user = storage.getUser();
  const borrowerId = user?.user?.userId;
  const [onboardingUrl, setOnboardingUrl] = useState("");

  const { isLoading, isFetching, data, refetch, error, isError } =
    useActiveOffersQuery({
      params: { ...params, search: debouncedSearch },
    });
  const [selectedSortLabel, setSelectedSortLabel] = useState("Newest First");
  const {
    data: applicationsData,
    isLoading: isAppsLoading,
    isError: isAppsError,
  } = useAllApplicationsQuery({ userId, params: {} });
  const offers: Offer[] = data?.offers || [];

  const {
    data: userData,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetUserQuery({ userId, params: {} });
  const appliedOfferIds = new Set(applicationsData?.applications?.map((app: any) => app.offerId));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      borrowerId: borrowerId ?? "",
      offerId: selectedOffer?.uuid ?? "",
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || "",
      email: userData?.email || "",
      phoneNumber: userData?.phoneNumber || "",
      pinCode: userData?.pinCode || "",
      address: userData?.address || "",
      country: userData?.country,
      state: userData?.state || "",
      city: userData?.city || "",
      region: userData?.region || "",
    },
    validationSchema: yup.object({
      borrowerId: yup.string().required("Borrower ID is required."),
      offerId: yup.string().required("Offer ID is required."),

      firstName: yup
        .string()
        .matches(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, apostrophes, and hyphens.")
        .min(2, "First name must be at least 2 characters.")
        .max(50, "First name must be at most 50 characters.")
        .required("First name is required."),

      lastName: yup
        .string()
        .matches(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, apostrophes, and hyphens.")
        .min(2, "Last name must be at least 2 characters.")
        .max(50, "Last name must be at most 50 characters.")
        .required("Last name is required."),

      email: yup
        .string()
        .email("Enter a valid email address.")
        .required("Email is required."),

      phoneNumber: yup
        .string()
        .required("Phone number is required")
        .matches(/^\+\d{6,15}$/, "Please enter valid phone number. "),

      pinCode: yup
        .string()
        .matches(/^[1-9][0-9]{3,5}$/, "Enter a valid pincode")
        .required("Pincode is required"),
      address: yup
        .string()
        .min(5, "Address must be at least 5 characters.")
        .max(200, "Address must be at most 200 characters.")
        .required("Address is required."),

      country: yup
        .string()
        .required("Country is required."),

      state: yup
        .string()
        .required("State is required."),

      city: yup
        .string()
        .required("City is required."),

      region: yup
        .string()
        .min(3, "Region must be at least 3 characters.")
        .max(200, "Region must be at most 200 characters.")
        .required("Region is required."),
    })
    ,
    onSubmit: async (values) => {
      setBtnLoading(true);
      try {
        const redirectUrl = getApplicationRedirectUrl(
          APPLICATION_REDIRECT_PATHS.browseOffers
        );

        const payload = { ...values, redirectUrl };
        const res = await createApplication(payload);
        const lenderId = res?.data?.offerData?.lenderId;
        const applicationId = res?.data?.applicationId;
        const offerData = res?.data?.offerData;

        const notificationMessage = `New loan application received from ${user?.user?.firstName} for the Offer: ${offerData?.title}.`;
        const notificationTitle = `New Application for "${offerData?.title}"`;

        try {
          await axios.post(
            `${envConfig.apiUrl}/notification/create-offer-notification`,
            {
              senderId: user?.user?.userId,
              senderType: "borrower",
              receiverType: "lender",
              receiverId: lenderId,
              title: notificationTitle,
              notification: notificationMessage,
              isGlobal: false,
            },
            {
              headers: {
                Authorization: `Bearer ${storage.getToken()}`,
              },
            }
          );
        } catch (apiErr) {
          console.error("Failed to save application notification:", apiErr);
        }

        if (socket?.connected && lenderId) {
          socket.emit("create application", {
            lenderId,
            borrowerId: user?.uuid,
            applicationId,
            details: values,
            notificationMessage,
          });
        }

        success(res?.data?.message || "Application submitted!");
        setIsModalOpen(false);
        navigate("/app/submitted-applications");
      } catch (err: any) {
        const errorData = err?.response?.data;
        if (errorData?.needsOnboarding && errorData?.onboardingUrl) {
          toast("You need to complete Stripe onboarding before applying.");
          setOnboardingUrl(errorData.onboardingUrl);
          return;
        }
        toast.error(errorData?.message || err.message || "Failed to submit application");
      } finally {
        setBtnLoading(false);

      };
    },
  });

  return (
    <div className="content dashboard ">
      <div className="content-wrapper borrower-dashboard-section">
        <h5 className="heading m-0 ofr-aaplicatn m-0">All Offers</h5>

        <div className="outer-box-wrapper">
          <div className="d-flex justify-content-between align-items-center title-dropdown-box borowwer-content">
            <div className="input-btn-fields d-flex align-items-center">
              <div className="search-box">
                <div className="input-search position-relative">
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search"
                    value={params.search}
                    onChange={(e) =>
                      setParams((prev) => ({ ...prev, search: e.target.value }))
                    }
                  />
                  <div className="search-icon position-absolute">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 13L9 9M1 5.66667C1 6.2795 1.12071 6.88634 1.35523 7.45252C1.58975 8.01871 1.93349 8.53316 2.36683 8.9665C2.80018 9.39984 3.31462 9.74358 3.88081 9.97811C4.447 10.2126 5.05383 10.3333 5.66667 10.3333C6.2795 10.3333 6.88634 10.2126 7.45252 9.97811C8.01871 9.74358 8.53316 9.39984 8.9665 8.9665C9.39984 8.53316 9.74358 8.01871 9.97811 7.45252C10.2126 6.88634 10.3333 6.2795 10.3333 5.66667C10.3333 5.05383 10.2126 4.447 9.97811 3.88081C9.74358 3.31462 9.39984 2.80018 8.9665 2.36683C8.53316 1.93349 8.01871 1.58975 7.45252 1.35523C6.88634 1.12071 6.2795 1 5.66667 1C5.05383 1 4.447 1.12071 3.88081 1.35523C3.31462 1.58975 2.80018 1.93349 2.36683 2.36683C1.93349 2.80018 1.58975 3.31462 1.35523 3.88081C1.12071 4.447 1 5.05383 1 5.66667Z"
                        stroke="#8F95A2"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className="dropdown-block gap-3 d-flex align-items-center position-relative">
              <button
                className="dropdown-toggle offer-dropdown d-flex justify-content-between align-items-center"
                onClick={() => setDropdownOpen(prev => !prev)}
              >
                <div className="ofr-date text-truncate">{selectedSortLabel}</div>
                <div className="dropdown-polygon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="16" height="16" fill="#FBFCFF" />
                    <path d="M7.99996 12.5602C7.7132 12.5602 7.42649 12.4507 7.20787 12.2322L0.328226 5.35249C-0.109409 4.91486 -0.109409 4.20531 0.328226 3.76785C0.765684 3.33039 1.47509 3.33039 1.91276 3.76785L7.99996 9.8554L14.0872 3.76806C14.5248 3.33061 15.2342 3.33061 15.6716 3.76806C16.1094 4.20552 16.1094 4.91507 15.6716 5.35271L8.79205 12.2324C8.57332 12.451 8.2866 12.5602 7.99996 12.5602Z" fill="#202224" />
                  </svg>

                </div>
              </button>

              {dropdownOpen && (
                <ul className="dropdown-menu offer-dropdown-list show" style={{ display: 'block', position: 'absolute' }}>
                  {[
                    { label: "Newest First", sortBy: "createdAt", sortOrder: "DESC" },
                    { label: "Oldest First", sortBy: "createdAt", sortOrder: "ASC" },
                    { label: "Loan Amount High to Low", sortBy: "loanAmount", sortOrder: "DESC" },
                    { label: "Loan Amount Low to High", sortBy: "loanAmount", sortOrder: "ASC" },
                  ].map((item) => (
                    <li key={item.label}>
                      <button
                        className={`dropdown-item ${selectedSortLabel === item.label ? "active" : ""}`}
                        onClick={() => {
                          setParams((prev) => ({
                            ...prev,
                            sortBy: item.sortBy,
                            sortOrder: item.sortOrder,
                          }));
                          setSelectedSortLabel(item.label);
                          setDropdownOpen(false);
                        }}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

          </div>
          <div className="active-offers-block browse-list d-flex flex-column">
            {isLoading || isFetching ? (
              <div>Loading...</div>
            ) : isError ? (
              <div>Error loading offers.</div>
            ) : offers?.length ? (
              offers.map((offer: any) => (
                <div key={offer.uuid} className="settlement-box m-0 mt-4">
                  <div className="applicant-info">
                    <div className="d-flex align-items-center justify-content-between settlement-block">
                      <div className="applicant-name-bloc d-flex align-items-center gap-3">
                        <div className="applicant-name-icon d-flex align-items-center justify-content-center">
                          <span>L</span>
                        </div>
                        <div className="applicant-name d-flex flex-column">
                          <span className="fw-bold">{offer.lender?.firstName && offer.lender?.lastName ? `${offer.lender.firstName} ${offer.lender.lastName}` : (offer.lender?.fullName || getAnonymousLenderLabel(offer.lender))}</span>
                          {offer.lender?.email && <span className="text-muted small" style={{ fontSize: "12px" }}>{offer.lender.email}</span>}
                        </div>
                      </div>
                      <div className="buttons-borwr-db d-flex align-items-center gap-2">
                        <div onClick={() => navigate("/app/review-offer", { state: { offerId: offer.uuid } })} className="review-btn">
                          <button className="review">Review</button>
                        </div>

                        {!appliedOfferIds.has(offer.uuid) ? (
                          <div className="apply-now">
                            <button
                              className="decline-btn"
                              onClick={() => {
                                setSelectedOffer(offer);
                                setIsModalOpen(true);
                              }}
                            >
                              Apply Now
                            </button>
                          </div>
                        ) : (
                          <div className="already-applied">
                            <button className="accept-btn">Already applied</button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="description">
                      <div className="applicant-infofields">
                        Title: <span className="title-db d-inline">{offer.title}</span>
                      </div>
                    </div>

                    <div className="loan-info d-flex align-items-center gap-3 flex-wrap">
                      <div className="applicant-infofields">
                        Loan Amount: <span>${offer.loanAmount}</span>
                      </div>
                      <div className="vr-line"></div>
                      <div className="applicant-infofields">
                        Interest: <span>{offer.interestRate}%</span>
                      </div>
                      <div className="vr-line"></div>
                      <div className="applicant-infofields">
                        Duration: <span>{offer.loanDuration} months</span>
                      </div>
                      <div className="vr-line"></div>
                      <div className="applicant-infofields">
                        Date: <span>{new Date(offer.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="vr-line"></div>
                      <div className="applicant-infofields">
                        Loan Duration: <span>{offer.loanDuration} months</span>
                      </div>
                    </div>

                    <div className="description">
                      <div className="applicant-infofields">
                        Description: <span>{offer.termsAndConditions}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">No open offers found.</div>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-dialog apply-now-modal modal-dialog-centered">
            <div className="modal-content">
              <div className="cross-btn d-flex justify-content-between align-items-center">
                <h1 className="heading m-0" id="applyLabel">Apply now</h1>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body p-0 mt-0">
                {selectedOffer && (
                  <>
                    <div className="settlement-box ofr-details-box mt-0 m-0">
                      <h6 className="m-0 settlement-date offer-details-box">Offer Details</h6>
                      <div className="completed-box-review position-relative overflow-hidden">
                        <h4 className="m-0">{selectedOffer.title}</h4>
                        <div className="loan-info d-flex align-items-center gap-3 flex-wrap">
                          <div className="applicant-infofields">
                            Loan Amount: <span>${selectedOffer.loanAmount}</span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Interest: <span>{selectedOffer.interestRate}%</span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Date: <span>{new Date(selectedOffer.createdAt).toLocaleDateString()}</span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Loan Duration: <span>{selectedOffer.loanDuration} months</span>
                          </div>
                        </div>
                        <div className="description">
                          <div className="applicant-infofields">
                            Description: <span>{selectedOffer.termsAndConditions}</span>
                          </div>
                        </div>
                        <div className="grouped-circle position-absolute">
                          <img src={groupedcircles} alt="" className="img-fluid" />
                        </div>
                      </div>
                    </div>

                    <div className="settlement-box your-details-box m-0">
                      <h6 className="m-0 settlement-date">Your Details</h6>
                      <form className="onboarding-form your-details-form" onSubmit={formik.handleSubmit}>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="firstName" className="form-label">
                              First Name<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="firstName"
                              {...formik.getFieldProps("firstName")}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="lastName" className="form-label">
                              Last Name<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="lastName"
                              {...formik.getFieldProps("lastName")}
                              required
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="email" className="form-label">
                              Email<span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              disabled
                              id="email"
                              {...formik.getFieldProps("email")}
                              required
                            />
                          </div>
                          {/* <div className="col-md-6">
                            <label htmlFor="phoneNumber" className="form-label">
                              Contact Number<span className="text-danger">*</span>
                            </label>
                            <input
                              type="tel"
                              className="form-control"
                              id="phoneNumber"
                              {...formik.getFieldProps("phoneNumber")}
                              required
                            />
                          </div> */}
                          <div className="col-lg-6 phone-number-label ">
                            <label className="form-label">Phone Number</label>
                            <PhoneInput
                              country="gb" // default country code
                              placeholder="Phone Number"
                              value={formik.values.phoneNumber}
                              onChange={(value) => {
                                // Normalize value with '+' prefix if missing
                                const normalized = value.startsWith('+') ? value : `+${value}`;
                                formik.setFieldValue('phoneNumber', normalized);
                              }}
                              onBlur={() => formik.setFieldTouched('phoneNumber', true)}
                              inputProps={{
                                name: 'phoneNumber',
                                required: true,
                                autoFocus: false,
                              }}
                              inputClass={formik.touched.phoneNumber && formik.errors.phoneNumber ? "input-error" : ""}
                            />
                            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                              <div className="error-message">{formik.errors.phoneNumber as string}</div>
                            )}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="creditScore" className="form-label">
                              Credit Score
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="creditScore"
                              value={userData?.creditScore || ""}
                              disabled
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="pinCode" className="form-label">
                              Pin code<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="pinCode"
                              {...formik.getFieldProps("pinCode")}
                              required
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label className="form-label">
                                Country <span>*</span>
                              </label>
                              <select
                                name="country"
                                className="form-control"
                                value={formik.values.country}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              >
                                <option value="">Select Country</option>
                                {countries.map((country) => (
                                  <option key={country.isoCode} value={country.isoCode}>
                                    {country.name}
                                  </option>
                                ))}
                              </select>
                              {formik.touched.country && formik.errors.country && (
                                <div className="text-danger">{formik.errors.country as string}</div>
                              )}
                              <div />
                            </div>

                            <div className="col-md-6">
                              <label htmlFor="state" className="form-label">
                                State<span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${formik.touched.state && formik.errors.state ? 'is-invalid' : ''}`}
                                id="state"
                                {...formik.getFieldProps("state")}
                                required
                              />
                              {formik.touched.state && formik.errors.state && (
                                <div className="invalid-feedback">{formik.errors.state as string}</div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="city" className="form-label">
                              City<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.city && formik.errors.city ? 'is-invalid' : ''}`}
                              id="city"
                              {...formik.getFieldProps("city")}
                              required
                            />
                            {formik.touched.city && formik.errors.city && (
                              <div className="invalid-feedback">{formik.errors.city as string}</div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="region" className="form-label">
                              Region<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.region && formik.errors.region ? 'is-invalid' : ''}`}
                              id="region"
                              {...formik.getFieldProps("region")}
                              required
                            />
                            {formik.touched.region && formik.errors.region && (
                              <div className="invalid-feedback">{formik.errors.region as string}</div>
                            )}
                          </div>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="address" className="form-label">
                            Address<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="address"
                            {...formik.getFieldProps("address")}
                            required
                          />
                        </div>
                        {onboardingUrl && (
                          <div className="alert-box alert-warning mt-4 text-center p-4 m-4">
                            <p className="mb-6">
                              Kindly complete the onboarding process before applying.
                            </p>
                            <div style={{ textAlign: "center", marginTop: "28px" }}>
                              <a style={{ cursor: "pointer" }}
                                onClick={() => {
                                  window.location.href = onboardingUrl; // 👈 open in same tab
                                }}

                                target="_blank"
                                rel="noopener noreferrer"
                                className="button-lender w-100 fw-normal text-decoration-none font-style-italic mt-6"
                              >
                                Complete Stripe Onboarding
                              </a>

                            </div>

                          </div>
                        )}
                        <div className="text-center">
                          <button type="submit" className="btn-apply" disabled={btnLoading}>
                            {btnLoading ? "Submitting..." : "Apply Now"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrowseOffers;
